import React, { useState, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    IconButton,
    Tooltip,
    Backdrop,
    CircularProgress,
    Box,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PageviewIcon from '@mui/icons-material/Pageview';
import DeleteIcon from "@mui/icons-material/Delete";
import { DataTable, Loadable } from '../../../../../common';
import { createDocumentStatusChipField, createDocumentTypeField, createNumberDataField, createStringDataField, createTimestampField, createTypeMetadataDataField } from "../../../../../util/format";
import { useData } from "@pure-ptr/react";
import { addManualAttachment, deleteAttachment, getPatientFhirDbData } from "../../../../../store/api/claimAnalysis";
import { errorToastify, infoToastify } from "../../../../../util/toast";
const DEFAULT_SORT = [{ id: 'documentName', orderBy: 'desc' }];
interface FormErrors {
    documentName?: string;    
    file?: string;
}

interface FormDataType {
    documentName: string;    
    documentType: number;
    file: File | null;
    fileBase64:any;
}

const fields = [
    createStringDataField("resourceId", "Name"),    
    createStringDataField("attachMentType", "Document Type"),
    createStringDataField("createdBy", "Uploaded By"),
    createTimestampField("createdAt", "Uploaded Time"),    
];

const handleView = (document) => {
    const base64Data =
        document?.attachMentData?.split(",")[1] || document?.attachMentData;    
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
        type: "application/pdf",
    });
    const fileURL = URL.createObjectURL(blob);
    window.open(fileURL, "_blank");
};

const UploadDocumentDialog = ({ open, handleClose, claimId}) => {    
    const [loading, setLoading] = useState(false);
    const [documents, setExistingDocuments] = useState([]);
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    const [attachment, setAttachment] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const initialFormData: FormDataType = {
    documentName: "",    
    documentType: 1,
    file: null,
    fileBase64: ""
};
    const [formData, setFormData] = useState<FormDataType>(initialFormData);
    const [errors, setErrors] = useState<FormErrors>({});
    const fileInputRef = useRef(null);

    const {
            isPending,
         } = useData(async () => {
            try {
                setErrors({});
                if (claimId && open) {                  
                    setLoading(true);
                    const response = await getPatientFhirDbData(claimId);
                    if (response) {
                        const documents = response.filter(
                            (doc) => doc.resourceType === "ManualUpload",
                        );
                        setExistingDocuments(documents);                        
                    }
                }
            }
            catch (error) {
                console.error(error);
            }
            finally {
                setLoading(false);
                setFormData(initialFormData);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            }
    
        }, [claimId, open, refreshFlag]);

    const {
        reload: runSubmitAttachment,
        isPending: isSubmitting,
        error
      } = useData(async () => {
        if (!isSubmit) return null;
        try {
          await addManualAttachment(attachment);          
        } catch (error) {
        errorToastify("Error in uploading attachment "+error);
        }
        finally {
          setIsSubmit(false);          
          setRefreshFlag(prev => !prev);
        }
      });   

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const selectedFile = e.target.files?.[0];
            if (!selectedFile) return;
            setErrors({});
            if (selectedFile.type !== "application/pdf") {
                setErrors((prev) => ({
                    ...prev,
                    file: "Only pdf files are allowed.",
                }));
                e.target.value = "";
                return;
            }
            const maxSize = 5 * 1024 * 1024;
            if (selectedFile.size > maxSize) {
                setErrors((prev) => ({
                    ...prev,
                    file: "File size should not exceed 5 MB.",
                }));
                e.target.value = "";
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                try {
                    const base64String = reader.result as string;
                    setFormData((prev) => ({
                        ...prev,
                        file: selectedFile,
                        fileBase64: base64String,
                    }));
                } catch (error) {
                    console.error("File processing error:", error);
                }
            };
            reader.onerror = () => {
                console.error("Error reading file");
                setErrors((prev) => ({
                    ...prev,
                    file: "Failed to read file.",
                }));
            };
            reader.readAsDataURL(selectedFile);
        } catch (error) {
            console.error("File upload error:", error);
            setErrors((prev) => ({
                ...prev,
                file: "Something went wrong while uploading file.",
            }));
        }
    };

    const validate = () => {
        try {
            const newErrors: FormErrors = {};
            const { documentName, file } = formData;
            if (!file) {
                newErrors.file = "Please select file";
            }
            if (errors.file) {
                newErrors.file = errors.file;
            }
            if (!documentName || documentName.trim() === "") {
                newErrors.documentName = "Please select document";
            }
            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        } catch (error) {
            console.error("Validation error:", error);
            return false;
        }
    };

    const handleSubmit = () => {
        try {
            if (!validate()) return;
            const file = formData.file;
            if (!file) {
                console.error("No file selected");
                return;
            }
            const fileName = file.name?.toLowerCase() || "";
            const extension = fileName.substring(fileName.lastIndexOf("."));
            const attachment = {
                resourceType: "ManualUpload",
                resourceId: formData.documentName,
                ClaimAnalysisDetailsId: claimId,
                attachmentType: extension,
                attachmentData: formData.fileBase64?.split(",")[1] || "",
            };
            setAttachment(attachment);
            setIsSubmit(true);
            runSubmitAttachment();
        } catch (error) {
            console.error("Submit error:", error);
        }
    };

    function documentActions(rowData) {
        return (
            <div className="d-flex">
                <IconButton
                    color="primary"
                    className="p-1"
                    aria-label="View"
                    onClick={() => handleView(rowData)}
                    size="large"
                >
                    <Tooltip title="View">
                        <PageviewIcon />
                    </Tooltip>
                </IconButton>

                <IconButton
                    color="primary"
                    className="p-1"
                    aria-label="Delete"
                    onClick={() => handleDelete(rowData)}
                    size="large"
                >
                    <Tooltip title="Delete">
                        <DeleteIcon />
                    </Tooltip>
                </IconButton>
            </div>
        );
    }
    const handleConfirmDelete = async () => {
        if (!selectedRow) return;
        setOpenDeleteDialog(false);
        setLoading(true);
        await deleteAttachment(selectedRow.id);
        setLoading(false);
        setRefreshFlag((prev) => !prev);        
        setSelectedRow(null);
    };
    const handleCloseDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedRow(null);
    };
    const handleDelete = async (rowData) => {
        setSelectedRow(rowData);
        setOpenDeleteDialog(true);        
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">Upload Attachment</Typography>            
            <Typography variant="body2" color="text.secondary">
                Supplementary Documents
            </Typography>
        </Box>
    </DialogTitle>

            <DialogContent>
                <Grid container spacing={1}>
                    {/* Upload section */}
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Document Name"
                                    name="documentName"
                                    value={formData?.documentName}
                                    onChange={handleChange}
                                    error={!!errors.documentName}
                                    helperText={errors.documentName}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Document Type</InputLabel>
                                    <Select
                                        name="documentType"
                                        value={formData?.documentType}
                                        onChange={handleChange}
                                        label="Document Type"
                                    >
                                        <MenuItem value={1}>PDF</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    type="file"                                    
                                    onChange={handleFileChange}
                                    inputProps={{
                                        ref: fileInputRef,
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    error={!!errors.file}
                                    helperText={errors.file}
                                    sx={{
                                        paddingTop: "16px",
                                        boxSizing: "border-box",
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    startIcon={<CloudUploadIcon />}
                                    onClick={handleSubmit}
                                >
                                    Upload
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    {/* Existing documents section */}
                    <Grid item xs={12}>
                        <DataTable
                            fields={fields}
                            data={documents}
                            sort={DEFAULT_SORT}
                            //onSort={""}
                            hasActions={true}
                            renderActionColumn={documentActions}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
            <Backdrop
                open={loading || Boolean(isPending) || Boolean(isSubmitting)}
                sx={{
                    zIndex: 9999,
                    color: "#fff",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <div style={{ marginTop: "50px" }}>
                    <CircularProgress
                        size={35}
                        thickness={2}
                        color="secondary"
                    />
                </div>
            </Backdrop>
            <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this attachment?
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="primary"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};
export default UploadDocumentDialog;
